import { useEffect, useState } from 'react'

import {
  fetchPartnerAttachmentsById,
  fetchPartnerFlagsById,
  fetchPartnerRequestById,
  type IPartner,
  type IPartnerFlags,
} from 'clickNgo/entities/partner'
import type { IAttachment } from 'clickNgo/models/file'
import Logger from 'core/utils/Logger'

interface IPartnerCompanyDetails {
  partnerId: string
  partner: IPartner | null
  partnerFlags: IPartnerFlags | null
  partnerAttachments: IAttachment[]
}

/* A shared empty list, so the memoised actions are not drawn again for a new empty one. */
const NO_ATTACHMENTS: IAttachment[] = []

/** Loads the company details the extended info shows for a partner once `isEnabled` is set, and
 *  keeps them: opened again for the same partner, the details stand at once. A partner that fails
 *  to load stays not found until the cockpit reloads. */
export const usePartnerCompanyDetails = (partnerId: string, isEnabled: boolean) => {
  const [details, setDetails] = useState<IPartnerCompanyDetails | null>(null)
  const loadedDetails = details?.partnerId === partnerId ? details : null
  const shouldLoad = isEnabled && Boolean(partnerId) && !loadedDetails

  useEffect(() => {
    if (!shouldLoad) {
      return
    }

    let isCancelled = false
    const options = { cacheKey: partnerId }

    Promise.all([
      fetchPartnerRequestById(partnerId, options),
      fetchPartnerFlagsById(partnerId, options),
      /* Only the partner editor reads the attachments: the details stand without them. */
      fetchPartnerAttachmentsById(partnerId, options).catch(() => NO_ATTACHMENTS),
    ])
      .then(
        ([partner, partnerFlags, partnerAttachments]): IPartnerCompanyDetails => ({
          partnerId,
          partner,
          partnerFlags,
          partnerAttachments,
        })
      )
      .catch((error: unknown): IPartnerCompanyDetails => {
        Logger.error('Error while loading partner details:', error)

        return { partnerId, partner: null, partnerFlags: null, partnerAttachments: NO_ATTACHMENTS }
      })
      .then((loaded) => {
        /* Closed, or another partner asked for, while this one was on its way. */
        if (!isCancelled) {
          setDetails(loaded)
        }
      })

    return () => {
      isCancelled = true
    }
  }, [partnerId, shouldLoad])

  return {
    isLoading: shouldLoad,
    partner: loadedDetails?.partner ?? null,
    partnerFlags: loadedDetails?.partnerFlags ?? null,
    partnerAttachments: loadedDetails?.partnerAttachments ?? NO_ATTACHMENTS,
  }
}
